import { css, html, LitElement } from 'lit';
import { RequesterMixin } from '@brightspace-ui/core/mixins/provider-mixin';

/**
 * Task Manager Component
 *
 * A component that displays all tasks for the current user with the ability to refresh.
 * Supports both determinate and indeterminate tasks.
 *
 * @example
 * <nova-task-manager></nova-task-manager>
 */
export class TaskManager extends RequesterMixin(LitElement) {
  static get properties() {
    return {
      environmentId: { type: String, reflect: true, attribute: 'environment-id' },
      tasks: { type: Array, state: true },
      loading: { type: Boolean, state: true },
      error: { type: String, state: true },
      statusFilter: { type: String, state: true },
      timeRangeFilter: { type: String, state: true },
      sortBy: { type: String, state: true },
      sortDirection: { type: String, state: true },
      expandedTasks: { type: Object, state: true },
      nextToken: { type: String, state: true },
      loadingMore: { type: Boolean, state: true },
    };
  }

  static get styles() {
    return css`
      :host {
        display: block;
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
        color: #1f2937;
      }

      .task-manager {
        background: #ffffff;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 0 16px 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        margin-bottom: 16px;
      }

      h2 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1f2937;
      }

      .controls {
        display: flex;
        gap: 10px;
        align-items: center;
      }

      .refresh-button {
        background: #7c3aed;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 8px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 500;
        transition: background 0.15s ease;
      }

      .refresh-button:hover {
        background: #6d28d9;
      }

      .refresh-button svg {
        width: 16px;
        height: 16px;
      }

      .refresh-button.loading {
        opacity: 0.7;
        cursor: not-allowed;
      }

      .refresh-button.loading svg {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .filter-control {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .filter-control label {
        font-size: 13px;
        color: #6b7280;
      }

      select {
        padding: 8px 12px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #f9fafb;
        font-size: 13px;
        color: #1f2937;
        cursor: pointer;
        outline: none;
        transition: border-color 0.15s ease;
      }

      select:focus {
        border-color: rgba(124, 58, 237, 0.4);
        box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
      }

      .task-list {
        max-height: none;
        overflow-y: visible;
      }

      .task-item {
        padding: 16px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        margin-bottom: 12px;
        background: #ffffff;
        transition: box-shadow 0.15s ease;
      }

      .task-item:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
      }

      .task-item:last-child {
        margin-bottom: 0;
      }

      .task-item-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
      }

      .task-title {
        font-weight: 600;
        font-size: 15px;
        margin: 0;
        color: #1f2937;
      }

      .task-type {
        color: #9ca3af;
        font-size: 12px;
        margin-top: 2px;
        font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
      }

      .task-status {
        padding: 4px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        border: 1px solid transparent;
      }

      .status-pending {
        background: #f3f4f6;
        color: #6b7280;
        border-color: #e5e7eb;
      }

      .status-in_progress {
        background: rgba(124, 58, 237, 0.1);
        color: #7c3aed;
        border-color: rgba(124, 58, 237, 0.2);
      }

      .status-completed {
        background: rgba(22, 163, 74, 0.1);
        color: #16a34a;
        border-color: rgba(22, 163, 74, 0.2);
      }

      .status-failed {
        background: rgba(220, 38, 38, 0.1);
        color: #dc2626;
        border-color: rgba(220, 38, 38, 0.2);
      }

      .progress-container {
        height: 6px;
        background: #f3f4f6;
        border-radius: 999px;
        overflow: hidden;
        margin: 12px 0;
      }

      .progress-bar {
        height: 100%;
        background: #7c3aed;
        border-radius: 999px;
        transition: width 0.3s ease;
      }

      .progress-bar.completed {
        background: #16a34a;
      }

      .progress-bar.failed {
        background: #dc2626;
      }

      .progress-bar.indeterminate {
        width: 50%;
        background: linear-gradient(to right, transparent, #7c3aed, transparent);
        animation: indeterminate-progress 1.5s infinite ease-in-out;
      }

      @keyframes indeterminate-progress {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }

      .task-details {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        font-size: 12px;
        color: #6b7280;
      }

      .task-details span {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .task-message {
        margin: 12px 0;
        font-size: 13px;
        color: #4b5563;
      }

      .task-data {
        margin-top: 12px;
        padding: 12px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        font-size: 12px;
      }

      .task-data pre,
      .completion-data pre {
        margin: 8px 0 0 0;
        font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
        font-size: 11px;
        white-space: pre-wrap;
        word-break: break-word;
      }

      .completion-data {
        margin-top: 12px;
        padding: 12px;
        background: rgba(22, 163, 74, 0.05);
        border: 1px solid rgba(22, 163, 74, 0.15);
        border-left: 3px solid #16a34a;
        border-radius: 8px;
        font-size: 12px;
      }

      .data-container {
        display: flex;
        gap: 12px;
        margin-top: 12px;
        overflow: hidden;
        max-height: 0;
        transition: max-height 0.3s ease-out;
      }

      .data-container.expanded {
        max-height: 500px;
        transition: max-height 0.5s ease-in;
      }

      .data-container > div {
        flex: 1;
        margin-top: 0;
      }

      .toggle-data-button {
        background: none;
        border: none;
        color: #7c3aed;
        cursor: pointer;
        font-size: 13px;
        font-weight: 500;
        padding: 0;
        display: flex;
        align-items: center;
        margin-top: 8px;
      }

      .toggle-data-button:hover {
        color: #6d28d9;
      }

      .toggle-data-button svg {
        width: 16px;
        height: 16px;
        margin-right: 4px;
        transition: transform 0.3s;
      }

      .toggle-data-button.expanded svg {
        transform: rotate(180deg);
      }

      .task-actions {
        margin-top: 12px;
        display: flex;
        gap: 8px;
      }

      .action-button {
        background: #7c3aed;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s ease;
      }

      .action-button:hover {
        background: #6d28d9;
      }

      .empty-state {
        padding: 48px 24px;
        text-align: center;
        color: #6b7280;
        background: #f9fafb;
        border-radius: 12px;
        border: 1px dashed #e5e7eb;
      }

      .empty-state p {
        margin: 0;
        font-size: 14px;
      }

      .error-message {
        padding: 12px 16px;
        background: rgba(220, 38, 38, 0.08);
        border: 1px solid rgba(220, 38, 38, 0.2);
        border-left: 3px solid #dc2626;
        border-radius: 8px;
        color: #991b1b;
        margin: 0 0 16px 0;
        font-size: 13px;
      }

      .loading-overlay {
        padding: 48px 24px;
        text-align: center;
        color: #6b7280;
      }

      .loading-spinner {
        display: inline-block;
        width: 32px;
        height: 32px;
        border: 3px solid #f3f4f6;
        border-radius: 50%;
        border-top-color: #7c3aed;
        animation: spin 1s linear infinite;
        margin-bottom: 12px;
      }

      .loading-overlay p {
        margin: 0;
        font-size: 14px;
      }

      .sort-button {
        background: none;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        color: #6b7280;
        font-size: 12px;
        padding: 4px 8px;
        border-radius: 6px;
        transition: background 0.15s ease;
      }

      .sort-button:hover {
        background: #f3f4f6;
      }

      .sort-icon {
        margin-left: 4px;
      }

      .pagination {
        display: flex;
        justify-content: center;
        padding: 16px;
        gap: 8px;
      }

      .pagination-button {
        padding: 6px 12px;
        border: 1px solid #e5e7eb;
        background: white;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
        transition: all 0.15s ease;
      }

      .pagination-button:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }

      .pagination-button.active {
        background: #7c3aed;
        color: white;
        border-color: #7c3aed;
      }

      .pagination-button.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .load-more-container {
        display: flex;
        justify-content: center;
        padding: 16px 0;
      }

      .load-more-button {
        background: #f3f4f6;
        color: #1f2937;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 10px 20px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .load-more-button:hover {
        background: #e5e7eb;
      }

      .load-more-button:disabled,
      .load-more-button.loading {
        opacity: 0.6;
        cursor: not-allowed;
      }
    `;
  }

  constructor() {
    super();
    this.tasks = [];
    this.loading = false;
    this.error = null;
    this.statusFilter = 'all';
    this.timeRangeFilter = 'all';
    this.sortBy = 'startTime';
    this.sortDirection = 'desc';
    this.expandedTasks = {};
    this.nextToken = null;
    this.loadingMore = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.client = this.requestInstance('d2l-pm-client');
    this.fetchTasks();
  }

  /**
   * Fetch all tasks for the current user/tenant
   * @param {boolean} showLoading - Whether to show loading state, defaults to true for manual refreshes
   * @param {boolean} loadMore - Whether this is a load more request
   */
  async fetchTasks(showLoading = true, loadMore = false) {
    if (showLoading && !loadMore) {
      this.loading = true;
    }

    if (loadMore) {
      this.loadingMore = true;
    }

    this.error = null;

    try {
      // Build filter object for the updated client method
      const filters = {
        limit: 20,
      };

      // Only add nextToken for load more requests
      if (loadMore && this.nextToken) {
        filters.nextToken = this.nextToken;
      } else if (loadMore && !this.nextToken) {
        // If trying to load more but no nextToken exists
        this.loadingMore = false;
        return;
      }

      // Only add status filter if not set to 'all'
      if (this.statusFilter && this.statusFilter !== 'all') {
        filters.status = this.statusFilter;
      }

      // Add time range filter if not set to 'all'
      if (this.timeRangeFilter && this.timeRangeFilter !== 'all') {
        // Send the timeRange directly to the API
        filters.timeRange = this.timeRangeFilter;
      }

      // Call the updated client method with filters
      const data = await this.client.fetchTasks(this.environmentId, filters);

      // Store the nextToken for pagination
      this.nextToken = data.nextToken || null;

      // For load more, append the new tasks to existing ones
      if (loadMore) {
        this.tasks = [...this.tasks, ...(data.tasks || [])];
      } else {
        // For initial load or refresh, replace the tasks
        this.tasks = data.tasks || [];
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      this.error = `Error: ${error.message}`;
    } finally {
      if (showLoading && !loadMore) {
        this.loading = false;
      }

      if (loadMore) {
        this.loadingMore = false;
      }
    }
  }

  /**
   * Manual refresh method
   */
  refresh() {
    this.nextToken = null; // Reset pagination
    this.fetchTasks(true, false);
  }

  /**
   * Handle status filter change
   */
  handleStatusFilterChange(e) {
    this.statusFilter = e.target.value;
    this.nextToken = null; // Reset pagination when filter changes
    this.fetchTasks();
  }

  /**
   * Handle time range filter change
   */
  handleTimeRangeFilterChange(e) {
    this.timeRangeFilter = e.target.value;
    this.nextToken = null; // Reset pagination when filter changes
    this.fetchTasks();
  }

  /**
   * Handle sort by change
   * @param {string} field - Field to sort by
   */
  handleSortChange(field) {
    if (this.sortBy === field) {
      // Toggle direction if clicking the same field
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortDirection = 'desc'; // Default to descending for new field
    }
  }

  /**
   * Set data for a completed task
   * @param {string} taskId - ID of the task to update
   * @param {Object} data - Data to set on the task
   */
  async setTaskData(taskId, data) {
    if (!taskId || !data) {
      console.error('Task ID and data are required');
      return false;
    }

    try {
      await this.client.updateTaskExecution(taskId, { data });
      // Refresh tasks to get updated data
      this.fetchTasks();
      return true;
    } catch (error) {
      console.error('Error updating task data:', error);
      this.error = `Error updating task: ${error.message}`;
      return false;
    }
  }

  /**
   * Filter tasks based on status
   */
  getFilteredTasks() {
    if (!this.tasks.length) return [];

    const filtered = [...this.tasks];

    // Apply sorting only - filters are now applied server-side
    filtered.sort((a, b) => {
      let valA = a[this.sortBy];
      let valB = b[this.sortBy];

      // Special case for dates
      if (this.sortBy === 'startTime' || this.sortBy === 'endTime' || this.sortBy === 'lastUpdated') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      }

      // Compare based on direction
      const compareResult = valA > valB ? 1 : valA < valB ? -1 : 0;
      return this.sortDirection === 'asc' ? compareResult : -compareResult;
    });

    return filtered;
  }

  /**
   * Format a date for display
   */
  formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleString();
  }

  /**
   * Calculate duration between start and end time
   */
  formatDuration(startTime, endTime, status, lastUpdated) {
    if (!startTime) return '';

    const start = new Date(startTime);
    // For completed or failed tasks, use the provided endTime or lastUpdated time
    // For in-progress tasks, use the current time
    let end;
    if (status === 'completed' || status === 'failed') {
      end = endTime ? new Date(endTime) : (lastUpdated ? new Date(lastUpdated) : new Date());
    } else {
      end = new Date();
    }

    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);

    if (seconds < 60) {
      return `${seconds} seconds`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  }

  /**
   * Get status message based on status and completion percentage
   * @private
   */
  _getStatusMessage(status, percentComplete) {
    if (status === 'pending') return 'Task scheduled';
    if (status === 'completed') return 'Task completed successfully';
    if (status === 'failed') return 'Task failed: An error occurred during processing';

    // For in-progress with percentComplete available
    if (percentComplete !== undefined && percentComplete !== null) {
      if (percentComplete < 20) return 'Initializing task resources...';
      if (percentComplete < 50) return 'Processing data...';
      if (percentComplete < 80) return 'Processing additional records...';
      return 'Finalizing results...';
    }

    // For indeterminate in-progress tasks
    return 'Processing...';
  }

  /**
   * Render progress bar for a task
   * @private
   */
  _renderProgressBar(task) {
    // For completed or failed tasks
    if (task.status === 'completed') {
      return html`<div class="progress-bar completed" style="width: 100%"></div>`;
    }

    if (task.status === 'failed') {
      return html`<div class="progress-bar failed" style="width: 100%"></div>`;
    }

    // For indeterminate tasks (when in_progress or pending)
    if (task.isIndeterminate && (task.status === 'in_progress' || task.status === 'pending')) {
      return html`<div class="progress-bar indeterminate"></div>`;
    }

    // For pending tasks
    if (task.status === 'pending') {
      return html`<div class="progress-bar" style="width: 0%"></div>`;
    }

    // For in-progress tasks
    if (task.percentComplete !== undefined && task.percentComplete !== null) {
      // Determinate progress
      return html`<div class="progress-bar" style="width: ${task.percentComplete}%"></div>`;
    } else {
      // Indeterminate progress as fallback
      return html`<div class="progress-bar indeterminate"></div>`;
    }
  }

  /**
   * Format task data for display
   * @private
   */
  _formatTaskData(data) {
    if (!data) return '';

    try {
      if (typeof data === 'string') {
        return data;
      }
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return `Error formatting data: ${error.message}`;
    }
  }

  /**
   * Load more tasks
   */
  loadMoreTasks() {
    if (this.nextToken && !this.loadingMore) {
      this.fetchTasks(false, true);
    }
  }

  render() {
    const filteredTasks = this.getFilteredTasks();

    return html`
      <div class="task-manager">
        <div class="header">
          <h2>Tasks</h2>
          <div class="controls">
            <div class="filter-control">
              <label for="status-filter">Status:</label>
              <select id="status-filter" @change="${this.handleStatusFilterChange}" .value="${this.statusFilter}">
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <button class="refresh-button ${this.loading ? 'loading' : ''}"
                    @click="${this.refresh}"
                    ?disabled="${this.loading}">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            </button>
          </div>
        </div>

        ${this.error ? html`<p class="error-message">${this.error}</p>` : ''}

        ${this.loading && !this.tasks.length ? html`
          <div class="loading-overlay">
            <div class="loading-spinner"></div>
            <p>Loading tasks...</p>
          </div>
        ` : ''}

        ${!this.loading && !filteredTasks.length ? html`
          <div class="empty-state">
            <p>No tasks found${this.statusFilter !== 'all' ? ` with status "${this.statusFilter}"` : ''}.</p>
          </div>
        ` : ''}

        ${filteredTasks.length ? html`
          <div class="task-list">
            ${filteredTasks.map(task => html`
              <div class="task-item">
                <div class="task-item-header">
                  <div>
                    <h3 class="task-title">${this._getTaskTitle(task.taskType)}</h3>
                    <div class="task-type">${task.taskType}</div>
                  </div>
                  <span class="task-status status-${task.status}">${task.status}</span>
                </div>

                <div class="progress-container">
                  ${this._renderProgressBar(task)}
                </div>

                ${task.statusMessage ? html`
                  <div class="task-message">${task.statusMessage}</div>
                ` : html`
                  <div class="task-message">${this._getStatusMessage(task.status, task.percentComplete)}</div>
                `}

                <div class="task-details">
                  <span>ID: ${task.id.substring(0, 8)}...</span>
                  <span>Started: ${this.formatDate(task.startTime)}</span>
                  <span>${task.endTime ? `Completed: ${this.formatDate(task.endTime)}` : `Duration: ${this.formatDuration(task.startTime, task.endTime, task.status, task.lastUpdated)}`}</span>
                </div>

                ${(task.data || task.completionData) ? html`
                  <button
                    class="toggle-data-button ${this.expandedTasks[task.id] ? 'expanded' : ''}"
                    @click="${() => this.toggleTaskData(task.id)}">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                    ${this.expandedTasks[task.id] ? 'Hide' : 'Show'} Data
                  </button>

                  <div class="data-container ${this.expandedTasks[task.id] ? 'expanded' : ''}">
                    ${task.data ? html`
                      <div class="task-data">
                        <strong>Task Data:</strong>
                        <pre>${this._formatTaskData(task.data)}</pre>
                      </div>
                    ` : ''}

                    ${task.completionData ? html`
                      <div class="completion-data">
                        <strong>Completion Data:</strong>
                        <pre>${this._formatTaskData(task.completionData)}</pre>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}

                ${task.status === 'completed' && task.taskType === 'task-execution' ? html`
                  <div class="task-actions">
                    <button class="action-button" @click="${() => this._showDataDialog(task.id)}">
                      Set Data
                    </button>
                  </div>
                ` : ''}
              </div>
            `)}

            ${this.nextToken ? html`
              <div class="load-more-container">
                <button
                  class="load-more-button ${this.loadingMore ? 'loading' : ''}"
                  @click="${this.loadMoreTasks}"
                  ?disabled="${this.loadingMore}">
                  ${this.loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Show dialog to set data for a task
   * @private
   */
  _showDataDialog(taskId) {
    // Create a simple prompt for data
    const data = prompt('Enter data for the task (as JSON or string):');
    if (data === null) return; // User cancelled

    try {
      // Try to parse as JSON, but fall back to string if it fails
      const parsedData = data.trim().startsWith('{') ? JSON.parse(data) : data;
      this.setTaskData(taskId, parsedData);
    } catch (error) {
      // If JSON parsing fails, use as string
      this.setTaskData(taskId, data);
    }
  }

  /**
   * Get a user-friendly title for a task type
   */
  _getTaskTitle(taskType) {
    // Convert kebab-case or snake_case to Title Case
    return taskType
      .replace(/[-_]/g, ' ')
      .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }

  /**
   * Toggle expanded state for a task
   * @param {string} taskId - ID of the task to toggle
   */
  toggleTaskData(taskId) {
    this.expandedTasks = {
      ...this.expandedTasks,
      [taskId]: !this.expandedTasks[taskId],
    };
  }
}

customElements.define('nova-task-manager', TaskManager);
